import { useEffect, useLayoutEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import GiftToast from '../../components/gifts/GiftToast'
import CreateBusinessModal from '../../components/businesses/CreateBusinessModal'
import { myBusinesses } from '../../api/businessesReader'
import type { BusinessProfile } from '../../types/businesses'
import { getToken } from '../../lib/auth'
import { clearCreateBusinessIntent, setCreateBusinessIntent } from '../../lib/pagesOnboarding'

export default function SettingsPages() {
  const authed = Boolean(getToken())
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [items, setItems] = useState<BusinessProfile[]>([])
  const [createOpen, setCreateOpen] = useState(false)
  const [toast, setToast] = useState<{ visible: boolean; label: string; detail?: string; kind?: 'success' | 'error' }>({
    visible: false,
    label: '',
    detail: undefined,
    kind: 'success',
  })
  const createRequested = searchParams.get('create') === '1'

  const hideToast = () => setToast((t) => ({ ...t, visible: false }))

  const load = async () => {
    if (!authed) {
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const rows = await myBusinesses()
      setItems(rows)
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load pages')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!authed) {
      if (createRequested) {
        setCreateBusinessIntent({ originPath: '/settings/pages' })
        navigate(`/login?return_to=${encodeURIComponent('/pages?create=1')}`)
      } else {
        navigate('/login')
      }
      return
    }
  }, [authed, createRequested, navigate])

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [])

  useEffect(() => {
    void load()
  }, [authed])

  useEffect(() => {
    if (!authed || !createRequested) return
    setCreateOpen(true)
  }, [authed, createRequested])

  const setCreateQuery = (enabled: boolean) => {
    const next = new URLSearchParams(searchParams)
    if (enabled) next.set('create', '1')
    else next.delete('create')
    setSearchParams(next, { replace: true })
  }

  if (!authed) return null

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-extrabold">Pixe Pages</h1>
          <div className="text-sm text-white/70">Create and manage your business pages.</div>
        </div>
        <button
          type="button"
          onClick={() => {
            setCreateOpen(true)
            setCreateQuery(true)
          }}
          className="inline-flex rounded-lg bg-brand px-3 py-2 text-sm font-semibold text-white hover:bg-brand-600"
        >
          Create Business Page
        </button>
      </div>

      <div className="rounded-xl border border-white/10 bg-white/5 p-4">
        {loading ? <div className="text-sm text-white/70">Loading...</div> : null}
        {!loading && error ? <div className="text-sm text-red-300">{error}</div> : null}
        {!loading && !error && items.length === 0 ? <div className="text-sm text-white/70">No business pages yet.</div> : null}
        {!loading && !error && items.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-white/60">
                  <th className="px-2 py-2">Name</th>
                  <th className="px-2 py-2">Category</th>
                  <th className="px-2 py-2">Slug</th>
                  <th className="px-2 py-2">Status</th>
                  <th className="px-2 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((b) => (
                  <tr key={b.id} className="border-t border-white/10">
                    <td className="px-2 py-2 font-semibold">{b.name}</td>
                    <td className="px-2 py-2 text-white/80">{b.category}</td>
                    <td className="px-2 py-2 text-white/70">{b.slug}</td>
                    <td className="px-2 py-2">
                      <span
                        className={`rounded-full border px-2 py-0.5 text-xs ${b.setup_fee_paid ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200' : 'border-amber-500/30 bg-amber-500/10 text-amber-200'}`}
                      >
                        {b.setup_fee_paid ? 'Active' : 'Pending'}
                      </span>
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex items-center gap-2">
                        <Link to={`/settings/pages/${b.slug}/edit`} className="rounded border border-white/15 bg-white/5 px-2 py-1 text-xs hover:bg-white/10">
                          Edit
                        </Link>
                        <Link to={`/settings/pages/${b.slug}`} className="rounded border border-white/15 bg-white/5 px-2 py-1 text-xs hover:bg-white/10">
                          Manage
                        </Link>
                        <Link to={`/b/${b.slug}`} className="rounded border border-white/15 bg-white/5 px-2 py-1 text-xs hover:bg-white/10">
                          View
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      <CreateBusinessModal
        open={createOpen}
        originPath="/settings/pages"
        onClose={() => {
          setCreateOpen(false)
          setCreateQuery(false)
        }}
        onCreated={(created) => {
          clearCreateBusinessIntent()
          setToast({ visible: true, label: 'Business page created', detail: `${created.name} is now active.`, kind: 'success' })
          setCreateOpen(false)
          setCreateQuery(false)
          void load()
          navigate(`/settings/pages/${created.slug}?tab=overview`)
        }}
      />

      <GiftToast
        visible={toast.visible}
        emoji={toast.kind === 'error' ? '!' : 'OK'}
        label={toast.label}
        detail={toast.detail}
        kind={toast.kind}
        onHide={hideToast}
      />
    </div>
  )
}
